import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HotToastService } from '@ngxpert/hot-toast';
import { KeycloakService } from 'keycloak-angular';
import { Subject } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  roles?: string[];
  claims?: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private router = inject(Router);
  private hotToast = inject(HotToastService);

  user = signal<User | null>(null);
  isAuthenticated = computed(() => !!this.user());

  /** Emits quando o armazém selecionado muda — componentes WMS podem subscrever. */
  readonly warehouseChange$ = new Subject<void>();
  private sessionExpirationHandled = false;
  private activityListeners: {
    handler: (event: Event) => void;
    documentEvents: string[];
    windowEvents: string[];
  } | null = null;
  private expirationIntervalId: ReturnType<typeof setInterval> | null = null;
  private lastThrottledActivityCheck = 0;

  // Controle de inatividade
  private lastActivityTime = 0;
  private idleTimeoutIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly IDLE_TIMEOUT_MINUTES = 15; // 15 minutos de inatividade = logout
  private readonly IDLE_CHECK_INTERVAL = 60_000; // Verifica a cada 1 minuto

  constructor(private keycloakService: KeycloakService) {
    const kc = this.keycloakService.getKeycloakInstance();
    if (kc.authenticated && kc.token && !this.isTokenInvalid(kc.token)) {
      this.hydrateUserFromTokenSync();
    }
  }

  /**
   * Preenche `user` só a partir do token em memória (sem rede — política passiva HML).
   */
  hydrateUserFromTokenSync(): void {
    const kc = this.keycloakService.getKeycloakInstance();
    const token = kc.token;
    if (!kc.authenticated || !token || this.isTokenInvalid(token)) {
      this.user.set(null);
      return;
    }

    const roles = this.keycloakService.getUserRoles(true);
    const tp = kc.tokenParsed as Record<string, unknown> | undefined;
    const first = (tp?.['given_name'] as string) || '';
    const last = (tp?.['family_name'] as string) || '';
    const username = (tp?.['preferred_username'] as string) || '';
    const email = (tp?.['email'] as string) || '';

    this.sessionExpirationHandled = false;
    this.user.set({
      id: kc.subject || '',
      name: `${first} ${last}`.trim() || username || 'User',
      email,
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(first)}+${encodeURIComponent(last)}&background=171717&color=fff`,
      roles,
      claims: tp,
    });

    this.registerActivityListeners();
    this.startTokenExpirationWatcher();
    this.startIdleTimeoutWatcher();
    this.updateLastActivityTime();
  }

  /** Mantido para chamadas existentes; não aciona /account nem refresh em rede (patch + hydrate local). */
  async loadUserProfile(): Promise<void> {
    const kc = this.keycloakService.getKeycloakInstance();
    if (!kc.authenticated) {
      this.user.set(null);
      this.removeActivityListeners();
      this.stopTokenExpirationWatcher();
      this.stopIdleTimeoutWatcher();
      return;
    }

    const token = kc.token;
    if (!token || this.isTokenInvalid(token)) {
      console.warn('[AuthService] Token inválido ao tentar carregar perfil. Forçando logout...');
      this.user.set(null);
      this.removeActivityListeners();
      this.stopTokenExpirationWatcher();
      this.handleSessionExpired();
      return;
    }

    try {
      this.hydrateUserFromTokenSync();
      void this.checkTokenAndLogoutIfExpired();
    } catch (error) {
      console.error('[AuthService] Erro ao hidratar perfil do usuário:', error);
      this.user.set(null);
      this.removeActivityListeners();
      this.stopTokenExpirationWatcher();
      this.handleSessionExpired();
    }
  }

  logout(): void {
    this.sessionExpirationHandled = false;
    this.user.set(null);
    this.removeActivityListeners();
    this.stopTokenExpirationWatcher();
    this.stopIdleTimeoutWatcher();

    try {
      this.keycloakService.logout(window.location.origin).catch(() => {
        this.forceRedirectToPublicPage();
      });
    } catch (error) {
      this.forceRedirectToPublicPage();
    }
  }

  private forceRedirectToPublicPage(): void {
    this.router.navigate(['/']).catch(() => {
      window.location.href = '/';
    });
  }

  updateUser(updates: Partial<User>): void {
    const currentUser = this.user();
    if (currentUser) {
      this.user.set({ ...currentUser, ...updates });
    }
  }

  getCurrentUser(): User | null {
    return this.user();
  }

  hasRole(role: string): boolean {
    const user = this.user();
    const normalized = role.toLowerCase();
    const fromSignal = (user?.roles || []).map((r) => r.toLowerCase());
    if (fromSignal.length > 0) {
      return fromSignal.includes(normalized);
    }
    return this.getRoles().map((r) => r.toLowerCase()).includes(normalized);
  }

  getUsername(): string | undefined {
    const profile = this.keycloakService.getKeycloakInstance().profile;
    return profile?.username;
  }

  getRoles(): string[] {
    const keycloak = this.keycloakService.getKeycloakInstance();
    const tokenParsed = keycloak?.tokenParsed as Record<string, unknown> | undefined;
    if (!tokenParsed) return [];

    const realmRoles =
      ((tokenParsed['realm_access'] as { roles?: string[] })?.roles) || [];
    const clientRoles =
      (
        (tokenParsed['resource_access'] as Record<string, { roles?: string[] }>)?.[
          environment.keycloak.clientId
        ]?.roles
      ) || [];

    return [...new Set([...realmRoles, ...clientRoles])];
  }

  hasMileExpressAccess(): boolean {
    const keycloak = this.keycloakService.getKeycloakInstance();
    const tokenParsed = keycloak?.tokenParsed as Record<string, any> | undefined;
    if (!tokenParsed) return false;

    const clientRoles: string[] =
      (tokenParsed['resource_access']?.['mile-express-front']?.roles as string[]) ?? [];
    if (clientRoles.length > 0) return true;

    const KEYCLOAK_DEFAULTS = new Set([
      'offline_access',
      'uma_authorization',
      'default-roles-mile-dev',
    ]);
    const realmRoles: string[] = (tokenParsed['realm_access']?.roles as string[]) ?? [];
    return realmRoles.some((r) => !KEYCLOAK_DEFAULTS.has(r));
  }

  getUserEmail(): string | undefined {
    const u = this.user();
    if (u?.email) return u.email;
    const keycloak = this.keycloakService.getKeycloakInstance();
    return (keycloak?.idTokenParsed as any)?.email || (keycloak?.tokenParsed as any)?.email;
  }

  getUserName(): string {
    const u = this.user();
    if (u?.name) return u.name;
    const keycloak = this.keycloakService.getKeycloakInstance();
    const profile = keycloak?.idTokenParsed as Record<string, string> | undefined;
    if (profile?.['name']) return profile['name'];
    if (profile?.['given_name']) {
      return `${profile['given_name']} ${profile['family_name'] || ''}`.trim();
    }
    try {
      return this.keycloakService.getUsername() ?? 'Usuário';
    } catch {
      return 'Usuário';
    }
  }

  getWarehouses(): { id: number; name: string }[] {
    const keycloak = this.keycloakService.getKeycloakInstance();
    const tokenParsed = (keycloak?.tokenParsed || keycloak?.idTokenParsed) as
      | Record<string, unknown>
      | undefined;
    const warehouses = tokenParsed?.['warehouses'];
    if (!warehouses || !Array.isArray(warehouses)) return [];
    return (warehouses as { id?: unknown; name?: unknown }[]).map((w) => ({
      id: Number(w.id),
      name: String(w.name ?? w.id),
    }));
  }

  private readonly WAREHOUSE_STORAGE_KEY = 'selectedWarehouseId';

  getSelectedWarehouseId(): number | null {
    const stored = sessionStorage.getItem(this.WAREHOUSE_STORAGE_KEY);
    if (stored === null) return null;
    const id = Number(stored);
    return isNaN(id) ? null : id;
  }

  setSelectedWarehouseId(id: number | null): void {
    if (id === null) {
      sessionStorage.removeItem(this.WAREHOUSE_STORAGE_KEY);
    } else {
      sessionStorage.setItem(this.WAREHOUSE_STORAGE_KEY, String(id));
    }
    this.warehouseChange$.next();
  }

  async getUserProfile(): Promise<{ name: string; email?: string; username?: string }> {
    const u = this.user();
    if (u) {
      return {
        name: u.name,
        email: u.email,
        username: u.email || this.getUsername(),
      };
    }
    const keycloak = this.keycloakService.getKeycloakInstance();
    const token = (keycloak?.idTokenParsed || keycloak?.tokenParsed) as
      | Record<string, string>
      | undefined;
    if (!token) {
      return { name: 'Usuário' };
    }
    const first = token['given_name'];
    const last = token['family_name'];
    const composed = first ? `${first} ${last || ''}`.trim() : undefined;
    return {
      name: composed || token['name'] || token['preferred_username'] || 'Usuário',
      email: token['email'],
      username: token['preferred_username'],
    };
  }

  private decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  }

  isTokenExpired(token?: string): boolean {
    try {
      const tokenToCheck = token || this.keycloakService.getKeycloakInstance().token;

      if (!tokenToCheck) {
        return true;
      }

      const decoded = this.decodeToken(tokenToCheck);
      if (!decoded || !decoded.exp) {
        return true;
      }

      const expirationTime = decoded.exp * 1000;
      const currentTime = Date.now();
      const bufferTime = 5000;

      return currentTime >= (expirationTime - bufferTime);
    } catch (error) {
      return true;
    }
  }

  isTokenInvalid(token?: string): boolean {
    try {
      const tokenToCheck = token || this.keycloakService.getKeycloakInstance().token;

      if (!tokenToCheck) {
        return true;
      }

      const parts = tokenToCheck.split('.');
      if (parts.length !== 3) {
        return true;
      }

      const decoded = this.decodeToken(tokenToCheck);
      if (!decoded) {
        return true;
      }

      if (!decoded.exp) {
        return true;
      }

      return this.isTokenExpired(tokenToCheck);
    } catch (error) {
      return true;
    }
  }

  async checkTokenAndLogoutIfExpired(): Promise<boolean> {
    try {
      const kc = this.keycloakService.getKeycloakInstance();
      if (!kc.authenticated) {
        this.user.set(null);
        this.removeActivityListeners();
        this.stopTokenExpirationWatcher();
        return false;
      }

      const token = kc.token;
      if (!token || this.isTokenInvalid(token)) {
        this.handleSessionExpired();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Erro ao verificar token:', error);
      this.handleSessionExpired();
      return false;
    }
  }

  handleSessionExpired(): void {
    if (this.sessionExpirationHandled) {
      return;
    }

    this.sessionExpirationHandled = true;
    console.warn(`[AuthService] ${this.timestamp()} Sessão expirada - usuário será desconectado.`);

    this.user.set(null);
    this.removeActivityListeners();
    this.stopTokenExpirationWatcher();
    this.stopIdleTimeoutWatcher();

    this.clearKeycloakLocalArtifacts();

    this.logout();
  }

  /** Limpa token em memória e chaves `kc-*` / `keycloak` no localStorage (keycloak-js). */
  private clearKeycloakLocalArtifacts(): void {
    try {
      this.keycloakService.getKeycloakInstance().clearToken();
      if (typeof Storage !== 'undefined') {
        for (const key of Object.keys(localStorage)) {
          if (key.includes('kc-') || key.includes('keycloak')) {
            localStorage.removeItem(key);
          }
        }
      }
    } catch (e) {
      console.warn('Erro ao limpar artefatos locais do Keycloak:', e);
    }
  }

  private handleUserActivity(eventType: string): void {
    this.updateLastActivityTime();
    // Refresh: apenas KeycloakBearerInterceptor + updateMinValidity (sem refresh paralelo aqui).
    const throttleMs = 30_000;
    const highFrequency = ['mousemove', 'scroll', 'touchstart'];
    if (highFrequency.includes(eventType)) {
      const now = Date.now();
      if (now - this.lastThrottledActivityCheck < throttleMs) {
        return;
      }
      this.lastThrottledActivityCheck = now;
    }
    void this.checkTokenAndLogoutIfExpired();
  }

  /**
   * Atualiza o timestamp da última atividade do usuário
   */
  private updateLastActivityTime(): void {
    this.lastActivityTime = Date.now();
  }

  /**
   * Verifica se o usuário está inativo há mais tempo que o permitido
   */
  private checkIdleTimeout(): void {
    if (this.lastActivityTime === 0) {
      // Se nunca houve atividade registrada, considera como atividade agora
      this.updateLastActivityTime();
      return;
    }

    const now = Date.now();
    const idleTime = now - this.lastActivityTime;
    const idleTimeoutMs = this.IDLE_TIMEOUT_MINUTES * 60 * 1000;

    if (idleTime >= idleTimeoutMs) {
      console.warn(
        `[AuthService] ${this.timestamp()} Usuário inativo há ${Math.floor(idleTime / 60000)} minutos. Forçando logout por inatividade.`
      );
      this.handleIdleTimeout();
    }
  }

  /**
   * Trata o timeout de inatividade - desloga o usuário
   */
  private handleIdleTimeout(): void {
    this.hotToast.warning(
      `Sessão encerrada: você ficou inativo por ${this.IDLE_TIMEOUT_MINUTES} minutos. Por segurança, sua sessão foi encerrada.`,
      { duration: 6000 }
    );

    this.handleSessionExpired();
  }

  /**
   * Inicia o watcher de timeout de inatividade
   */
  private startIdleTimeoutWatcher(): void {
    this.stopIdleTimeoutWatcher();
    this.updateLastActivityTime(); // Inicializa com o tempo atual

    this.idleTimeoutIntervalId = setInterval(() => {
      // Só verifica se a página estiver visível
      if (!document.hidden) {
        this.checkIdleTimeout();
      }
    }, this.IDLE_CHECK_INTERVAL);
  }

  /**
   * Para o watcher de timeout de inatividade
   */
  private stopIdleTimeoutWatcher(): void {
    if (this.idleTimeoutIntervalId) {
      clearInterval(this.idleTimeoutIntervalId);
      this.idleTimeoutIntervalId = null;
    }
    this.lastActivityTime = 0;
  }

  private registerActivityListeners(): void {
    this.removeActivityListeners();

    const handler = (event: Event) => this.handleUserActivity(event.type);
    const documentEvents = ['click', 'keydown', 'mousemove', 'touchstart', 'scroll'];
    const windowEvents = ['focus', 'blur'];

    documentEvents.forEach((eventName) => {
      const options: AddEventListenerOptions | boolean | undefined =
        eventName === 'scroll' || eventName === 'touchstart' || eventName === 'mousemove'
          ? { passive: true }
          : undefined;
      document.addEventListener(eventName, handler, options);
    });

    windowEvents.forEach((eventName) => window.addEventListener(eventName, handler));

    this.activityListeners = { handler, documentEvents, windowEvents };
  }

  private removeActivityListeners(): void {
    if (!this.activityListeners) {
      return;
    }

    const { handler, documentEvents, windowEvents } = this.activityListeners;
    documentEvents.forEach((eventName) => document.removeEventListener(eventName, handler));
    windowEvents.forEach((eventName) => window.removeEventListener(eventName, handler));
    this.activityListeners = null;
  }

  private startTokenExpirationWatcher(): void {
    this.stopTokenExpirationWatcher();
    this.expirationIntervalId = setInterval(() => {
      void this.checkTokenAndLogoutIfExpired();
    }, 15_000);
  }

  private stopTokenExpirationWatcher(): void {
    if (this.expirationIntervalId) {
      clearInterval(this.expirationIntervalId);
      this.expirationIntervalId = null;
    }
  }

  private timestamp(): string {
    return new Date().toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }
}
